import React, { useEffect, useState, useRef, useCallback } from 'react';
import Navbar from '../../navbar';
import Header from '../../header';
import { useDispatch, useSelector } from 'react-redux';
import classes from './style.module.css'
import { postApplication, safePdf, getFormMandatoryFields } from '../../../store/reducers/applicationSlice';
import { createNotification } from '../../../store/reducers/notificationSlice';
import FormHeader from '../forms/formHeader/formHeader';
import FormFooter from '../forms/formFooter/formFooter';
import AddAttachment from '../applications/attachments';
import FormLoader from '../forms/formLoader';
import { useNavigate, useParams } from "react-router-dom";
import { showSuccess, showError } from '../../../helpers/toast';
import { prepareHtmlForPdf } from '../../../utility/utility';

const AppForm = () => {
  const [formIds, setFormIds] = useState([]);
  const [inputFields, setInputFields] = useState({});
  const [pageIds, setPageIds] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const accessToken = useSelector((state) => state.auth.user.access_token);
  const user = useSelector((state) => state.auth.user);
  const { applyingFormData } = useSelector((state) => state.counter)
  const [signatureIds, setSignatureIds] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [showAttachments, setShowAttachments] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  const dispatch = useDispatch();

  // Reference to attachments component's saveChanges function
  const attachmentsRef = useRef(null);

  const navigate = useNavigate();
  const params = useParams();

  // Page visibility management - hide all pages and show only current page
  useEffect(() => {
    if (pageIds?.length > 0) {
      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        // Hide all pages first
        pageIds.forEach(id => {
          const pageDiv = document.getElementById(id);
          if (pageDiv) {
            pageDiv.style.display = "none";
          }
        });

        // Show only the current page
        const currentPageDiv = document.getElementById(`page${currentPage}`);
        if (currentPageDiv) {
          currentPageDiv.style.display = "block";
        }
      });
    }
  }, [pageIds, currentPage]);

  // Initialize first page when form loads
  useEffect(() => {
    if (pageIds?.length > 0 && currentPage === 1) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        // Show first page when form loads
        const firstPageDiv = document.getElementById('page1');
        if (firstPageDiv) {
          firstPageDiv.style.display = "block";
        }
      }, 100);
    }
  }, [pageIds, currentPage]);



  const [isLoading, setIsLoading] = useState(false);



  const handleInputChange = useCallback((event) => {
    const { id, value } = event.target;
    setInputFields(prevInputFields => ({
      ...prevInputFields,
      [id]: value,
    }));

  }, []);

  const fetchApplicationMandatoryFields = async () => {
    const data = await getFormMandatoryFields(params.id, accessToken);
    if (data) {
      console.log(data[0]?.mandatory_fields);
      return data[0]?.mandatory_fields;
    }
  };

  const handleCheckBoxChange = useCallback((event) => {
    const { id, checked, value } = event.target;

    setInputFields(prevInputFields => {
      // If the checkbox is checked, add it to inputFields
      if (checked) {
        return {
          ...prevInputFields,
          [id]: value,
        };
      } else {
        // If the checkbox is unchecked, remove it from inputFields
        const { [id]: removedValue, ...remainingFields } = prevInputFields;
        return remainingFields;
      }
    })
  }, []);

  const attachEventHandlers = useCallback(() => {
    let tempSignatureIds = []
    formIds.forEach(id => {
      const inputField = document.getElementById(id);
      if (inputField) {
        if (inputField.type === 'image') {
          tempSignatureIds.push(id);

        } else if (inputField.type === 'checkbox') {
          inputField.addEventListener('change', handleCheckBoxChange)
        }
        else {
          inputField.addEventListener('change', handleInputChange);
        }
      }

    });
    setSignatureIds(tempSignatureIds);
  }, [formIds, handleInputChange, handleCheckBoxChange]);

  useEffect(() => {
    if (formIds?.length > 0) {
      attachEventHandlers();
    }
  }, [formIds?.length, attachEventHandlers]);

  const areAllMandatoryFieldsFilled = (inputFields, mandatoryFields) => {
    return mandatoryFields.every((fieldId) => {
      const value = inputFields[fieldId];
      return value !== undefined && value !== null && value !== '';
    });
  };

  async function handleSubmitWithLoading(e) {
    setIsLoading(true);
    const mandatory_fields = await fetchApplicationMandatoryFields();
    let isValid = true;
    if (mandatory_fields) {
      isValid = areAllMandatoryFieldsFilled(inputFields, mandatory_fields);
    }
    if (!isValid) {
      showError("Please fill all mandatory fields.");
      setIsLoading(false);
    } else {
      let htmlForm = document.getElementById("DocumentPDF").outerHTML;
      // Determine application state based on user role
      const applicationState = user.user?.user_role === "employee" ? "application_submitted_by_company_user_to_company" : "in_process";
      console.log('User role:', user.user?.user_role, 'Setting application state to:', applicationState);

      const data = await dispatch(postApplication({
        form: applyingFormData?.name,
        json_data: inputFields,
        name: applyingFormData?.name,
        category: applyingFormData?.category,
        user_id: user.user.id,
        token: accessToken,
        files: uploadedFiles,
        state: applicationState
      }))

      console.log('Post application response:', data);

      // Check if the response has the expected structure
      if (data && data.payload && data.payload.id) {
        dispatch(safePdf({
          htmlForm: prepareHtmlForPdf(htmlForm, inputFields, pageIds),
          user: { id: user.user.id },
          id: data.payload.id,
          token: accessToken
        }))

        // If employee submitted, notify company admin
        if (user.user?.user_role === "employee") {

          // Notify Company Admin
          dispatch(createNotification({
            user_id: user.user.id,
            applicationId: data.payload.id,
            applicationName: applyingFormData?.name,
            status: "submitted",
            accessToken: accessToken,
            sendToCompanyAdmins: true,
            companyName: user.user?.company_name,
            employeeName: user.user?.full_name
          }));



          // Show success message for employee
          showSuccess('Application submitted successfully');
        } else {
          // Show success message for company admin
          showSuccess('Application submitted successfully!');

          // Notify Company Admin
          dispatch(createNotification({
            user_id: user.user.id,
            applicationId: data.payload.id,
            applicationName: applyingFormData?.name,
            status: "company_submitted",
            accessToken: accessToken,
            sendToCompanyAdmins: true,
            sendToGamingBoardAdmins: true,
            sendToGamingBoardReviewers: true,
            sendToGamingBoardViewers: true,
            companyName: user.user?.company_name,
            employeeName: user.user?.full_name
          }));
        }
      } else {
        console.error('Invalid response structure from postApplication:', data);
        showError('Application submitted but there was an issue with the response');
      }
      setIsLoading(false);

      // Navigate based on user role
      if (user.user?.user_role === "employee") {
        navigate('/in-review'); // Employee redirected to in-process (they can't see review)
      } else {
        navigate('/in-process-applications'); // Company admin applications go to in-process
      }
    }

  };

  async function saveToDrafts(e) {
    setIsLoading(true);
    let htmlForm = document.getElementById("DocumentPDF").outerHTML;
    const data = await dispatch(postApplication({
      form: applyingFormData?.name,
      json_data: inputFields,
      name: applyingFormData?.name,
      category: applyingFormData?.category,
      user_id: user.user.id,
      token: accessToken,
      files: uploadedFiles,
      state: e
    }))

    dispatch(safePdf({
      htmlForm: prepareHtmlForPdf(htmlForm, inputFields, pageIds),
      user: { id: user.user.id },
      id: data.payload.id,
      token: accessToken
    }))
    setIsLoading(false);
    dispatch(createNotification({
      user_id: user.user.id,
      applicationId: data.payload.id,
      applicationName: applyingFormData?.name,
      status: "drafts",
      accessToken: accessToken,
      sendToCompanyAdmins: true
    }))
    navigate(`/draft/${data.payload.id}`);
  };

  // Function to call saveChanges from attachments component
  const handleSaveChanges = async () => {
    if (attachmentsRef.current && attachmentsRef.current.saveChanges) {
      console.log('Calling saveChanges from attachments component');
      await attachmentsRef.current.saveChanges();
    } else {
      console.log('Attachments component or saveChanges function not available');
    }
  };
  const previousPage = () => {
    if (currentPage === pageIds?.length + 1) {
      setShowAttachments(false);
      showDiv(currentPage - 1);
      setCurrentPage(currentPage - 1)
    } else {
      if (currentPage - 1 >= 1) {
        hideDiv(currentPage);
        showDiv(currentPage - 1);
        setCurrentPage(currentPage - 1);
      }
    }
  }

  const nextPage = () => {
    if (currentPage + 1 <= pageIds?.length) {
      hideDiv(currentPage);
      showDiv(currentPage + 1);
      setCurrentPage(currentPage + 1);
    } else if (currentPage + 1 <= pageIds?.length + 1) {
      hideDiv(currentPage);
      setCurrentPage(currentPage + 1);
      setShowAttachments(true);
    }
  }
  const hideDiv = (pageNo) => {
    const pageDiv = document.getElementById("page" + pageNo);
    pageDiv.style.display = "none";
  }

  const showDiv = (pageNo) => {
    const pageDiv = document.getElementById("page" + pageNo);
    pageDiv.style.display = "";
  }

  const handlePageChange = (number) => {
    if (number > 0 && number <= pageIds?.length + 1) {
      if (number === pageIds?.length + 1) {
        // Hide current page and show attachments
        hideDiv(currentPage);
        setCurrentPage(number);
        setShowAttachments(true);
      } else {
        // Hide attachments and show the requested page
        setShowAttachments(false);

        // Hide current page and show new page
        if (currentPage !== pageIds?.length + 1) {
          hideDiv(currentPage);
        }

        // Small delay to ensure smooth transition
        setTimeout(() => {
          showDiv(number);
          setCurrentPage(number);
        }, 50);
      }
    }
  }



  return (
    <div>
      <Navbar />
      <Header />
      <div className='wrapper'>
        <div className='boxAppBoard boxWhite'>
          <div className="stickyFormHeader">
            <FormHeader
              applicationName={applyingFormData.name}
              nextPage={nextPage}
              previousPage={previousPage}
              currentPage={currentPage}
              totalPages={pageIds?.length + 1}
              handleSubmitWithLoading={handleSubmitWithLoading}
              saveToDrafts={saveToDrafts}
              handlePageChange={handlePageChange}
              saveChanges={handleSaveChanges}
            />
          </div>

          <div className={classes['board-content']}>

            {/* {pageIds.length > 0 && (
              <div style={{
                marginBottom: '20px',
                padding: '10px',
                backgroundColor: '#f8f9fa',
                borderRadius: '5px',
                textAlign: 'center'
              }}>
                <span style={{ fontWeight: 'bold', color: '#007bff' }}>
                  Page {currentPage} of {pageIds.length + 1}
                  {currentPage === pageIds.length + 1 && ' (Attachments)'}
                </span>
              </div>
            )} */}

            {/* Form content with page navigation styles */}
            <div style={{ position: 'relative' }}>
              <style>
                {`
                  /* Hide all pages by default */
                  [id^="page"] {
                    display: none !important;
                  }
                  
                  /* Show only the current page */
                  #page${currentPage} {
                    display: block !important;
                  }
                  
                  /* Ensure proper page styling */
                  [id^="page"] {
                    width: 100%;
                    min-height: 400px;
                  }
                `}
              </style>
              {isLoading && (
                <div className="application-spinner">
                  <div className="spinner "></div>
                </div>
              )}
              <FormLoader applicationName={applyingFormData.name} setFormIds={setFormIds} setPageIds={setPageIds} setIsLoaded={setIsLoaded} />
            </div>
          </div>
          {showAttachments && (
            <AddAttachment
              ref={attachmentsRef}
              signatureIds={signatureIds}
              uploadedFiles={uploadedFiles}
              setUploadedFiles={setUploadedFiles}
              applicationState="draft"
              applicationName={applyingFormData.name}
              jsonData={inputFields}
              embedded={true}
            />
          )}



          <FormFooter
            applicationName={applyingFormData.name}
            nextPage={nextPage}
            previousPage={previousPage}
            currentPage={currentPage}
            totalPages={pageIds?.length + 1}
            handleSubmitWithLoading={handleSubmitWithLoading}
            saveToDrafts={saveToDrafts}
            handlePageChange={handlePageChange}
            saveChanges={handleSaveChanges}
          />
        </div>
      </div>
    </div>
  )
}
export default AppForm